/**
 * What a render pass IS, in a few words.
 *
 * `renderer.render(scene, camera)` is called once per pass, and the scene it
 * gets is usually anonymous — so the GPU timing table and the render graph both
 * ended up showing six rows that all said "Scene", which is the TYPE of an
 * unnamed scene and tells you nothing.
 *
 * What a pass really is normally sits one level in: a post-processing pass is a
 * scene holding a single fullscreen quad, and the quad's MATERIAL is the pass —
 * "EdgeDetectionMaterial", "TiltShift.Target". So: the scene's own name if it
 * has one, else its single child's name, else that child's material.
 *
 * Deliberately shallow. One hop finds the quad (often wrapped in a Group);
 * digging further would start naming passes after arbitrary interior nodes.
 */

interface ObjectLike {
    name?: unknown;
    type?: unknown;
    children?: readonly unknown[];
    material?: unknown;
    isScene?: boolean;
    isMesh?: boolean;
}

const text = (value: unknown): string | undefined => {
    const s = typeof value === "string" ? value.trim() : "";
    return s.length > 0 ? s : undefined;
};

interface MaterialLike { name?: unknown; type?: unknown; isShaderMaterial?: boolean; isRawShaderMaterial?: boolean }

/** The first material, when a mesh has several. */
function firstMaterial(object: ObjectLike | null | undefined): MaterialLike | undefined {
    const material = object?.material;
    return (Array.isArray(material) ? material[0] : material) as MaterialLike | undefined;
}

/**
 * The name of a mesh's material — or, for a hand-written shader with no name,
 * what it IS. An unnamed ShaderMaterial on a fullscreen quad is the commonest
 * post pass there is, and "Mesh" says nothing about it.
 */
function materialName(object: ObjectLike | null | undefined): string | undefined {
    const material = firstMaterial(object);
    const named = text(material?.name);
    if (named) return named;
    if (!material) return undefined;
    const custom = material.isShaderMaterial === true
        || material.isRawShaderMaterial === true
        || material.type === "ShaderMaterial"
        || material.type === "RawShaderMaterial";
    return custom ? "Custom Shader" : undefined;
}

/** name → material → nothing. Used for whatever object the pass boils down to. */
function describeObject(object: ObjectLike | null | undefined): string | undefined {
    return text(object?.name) ?? materialName(object);
}

/**
 * A label for the object handed to `render()`, or undefined when it is not an
 * object we can say anything about.
 */
export function describeRenderPass(target: unknown): string | undefined {
    const object = target as ObjectLike | null | undefined;
    if (!object || typeof object !== "object") return undefined;
    if (typeof object.type !== "string" && object.isScene !== true && object.isMesh !== true) return undefined;

    const own = describeObject(object);
    if (own) return own;

    // exactly one child: that child is what this pass draws. A Group in between
    // is common enough to be worth the single extra hop.
    const children = Array.isArray(object.children) ? object.children : [];
    if (children.length === 1) {
        const child = children[0] as ObjectLike;
        const named = describeObject(child);
        if (named) return named;

        const grandchildren = Array.isArray(child?.children) ? child.children : [];
        if (grandchildren.length === 1) {
            const named = describeObject(grandchildren[0] as ObjectLike);
            if (named) return named;
        }
        // nothing is named — say WHAT it draws rather than nothing at all
        return text(child?.type) ?? text(object.type);
    }

    return text(object.type);
}
